import React, { useMemo } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { hooks, ApplicationThemeProvider, ProductLine } from '@adc/ui-core'
import configment from '@adc/configment'
import Header from '../header'
import { ApplicationContextProvider } from './context'
import ApplicationErrorBoundary from './errorBoundary'
import SharedPageMetadata from './sharedPageMetadata'
import { DEFAULT_CAPABILITIES } from './shell'

const FocusedAuctionApplicationShell = ({
  children,
  user,
  sspConfig,
  helmetConfig,
  featureFlags,
  capabilities, // see comment in ./shell.js for more info on capabilities
  theme = {
    productLine: ProductLine.AuctionCom,
    version: 1,
  },
}) => {
  hooks.useMountEffect(() => {
    document.body.setAttribute('data-app-state', 'ready')
    document.body.setAttribute('data-app-id', sspConfig.APP_NAME)
  })

  const productLineFromHost = hooks.useProductLineFromHost()

  const memoizedCapabilities = useMemo(() => {
    /**
     * Get accessibility options from configment per product line e.g smartsale, auctioncom.
     * in format of { equalweb: { site_key: 'site key', enabled: true } }
     */
    const accessibility =
      configment.get('accessibility', {})[productLineFromHost] || {}
    return _.merge(
      {},
      DEFAULT_CAPABILITIES,
      {
        accessibility,
      },
      capabilities
    )
  }, [capabilities, productLineFromHost])

  return (
    <ApplicationContextProvider
      sspConfig={sspConfig}
      user={user}
      featureFlags={featureFlags}
    >
      <ApplicationThemeProvider theme={theme}>
        <ApplicationErrorBoundary>
          <SharedPageMetadata
            helmetConfig={helmetConfig}
            sspConfig={sspConfig}
            featureFlags={featureFlags}
            accessibilityOptions={memoizedCapabilities.accessibility}
          />
          <Header.LogoHeader isSticky />
          {children}
        </ApplicationErrorBoundary>
      </ApplicationThemeProvider>
    </ApplicationContextProvider>
  )
}

FocusedAuctionApplicationShell.propTypes = {
  user: PropTypes.object,
  sspConfig: PropTypes.object,
  helmetConfig: PropTypes.object,
  featureFlags: PropTypes.object,
  children: PropTypes.node.isRequired,
  capabilities: PropTypes.shape({
    accessibility: PropTypes.shape({
      equalweb: PropTypes.object,
    }),
  }),
  theme: PropTypes.object,
}

export default FocusedAuctionApplicationShell
